import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Copy, Plus, Trash } from "@/components/icons";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
  Textarea,
  confirm,
  toast,
} from "@/components/kit";
import {
  FieldContent,
  FieldLabel,
  Field as FieldLayout,
} from "@/components/ui/field";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useState } from "react";
import {
  useChatSnippetsQuery,
  useCreateChatSnippet,
  useDeleteChatSnippet,
  useUpdateChatSnippet,
} from "../api/queries";
import { copyToClipboard } from "../lib/copy-to-clipboard";

type SnippetForm = { title: string; body: string; sortOrder: string };

const emptyForm: SnippetForm = { title: "", body: "", sortOrder: "0" };

export function ChatSnippetsPage() {
  const canManage = useActionPermission(
    ["snippets:write", "orders:write"],
    "any",
  );
  const query = useChatSnippetsQuery();
  const createSnippet = useCreateChatSnippet();
  const updateSnippet = useUpdateChatSnippet();
  const deleteSnippet = useDeleteChatSnippet();

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<SnippetForm>(emptyForm);

  const snippets = query.data?.data ?? [];

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  }

  function openEdit(snippet: {
    id: string;
    title: string;
    body: string;
    sortOrder: number;
  }) {
    setEditingId(snippet.id);
    setForm({
      title: snippet.title,
      body: snippet.body,
      sortOrder: String(snippet.sortOrder),
    });
    setOpen(true);
  }

  async function handleSave() {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("ກະລຸນາໃສ່ຊື່ ແລະ ຂໍ້ຄວາມ");
      return;
    }
    const sortOrder = Number(form.sortOrder) || 0;
    if (editingId) {
      updateSnippet.mutate(
        {
          id: editingId,
          input: {
            title: form.title.trim(),
            body: form.body.trim(),
            sortOrder,
          },
        },
        {
          onSuccess: () => {
            toast.success("ບັນທຶກແລ້ວ");
            setOpen(false);
          },
          onError: (e) =>
            toast.error("ບັນທຶກບໍ່ສຳເລັດ", {
              description: e instanceof Error ? e.message : undefined,
            }),
        },
      );
    } else {
      createSnippet.mutate(
        {
          title: form.title.trim(),
          body: form.body.trim(),
          sortOrder,
        },
        {
          onSuccess: () => {
            toast.success("ເພີ່ມ snippet ແລ້ວ");
            setOpen(false);
          },
          onError: (e) =>
            toast.error("ເພີ່ມບໍ່ສຳເລັດ", {
              description: e instanceof Error ? e.message : undefined,
            }),
        },
      );
    }
  }

  async function handleDelete(id: string) {
    const ok = await confirm({
      title: "ລຶບ snippet?",
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    deleteSnippet.mutate(id, {
      onSuccess: () => toast.success("ລຶບແລ້ວ"),
      onError: (e) =>
        toast.error("ລຶບບໍ່ສຳເລັດ", {
          description: e instanceof Error ? e.message : undefined,
        }),
    });
  }

  async function handleCopy(body: string) {
    const ok = await copyToClipboard(body);
    if (ok) toast.success("Copy ແລ້ວ");
    else toast.error("Copy ບໍ່ສຳເລັດ");
  }

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              ຂໍ້ຄວາມແຊດ
            </h2>
            <p className="text-muted-foreground text-sm">
              Snippet ທັກທາຍ / ແຈ້ງສົ່ງ — ກົດ Copy ແລ້ວ Paste ໃນ Facebook
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          {canManage ? (
            <Button className="rounded-full" onClick={openCreate}>
              <Plus data-icon="inline-start" />
              ເພີ່ມ snippet
            </Button>
          ) : null}
        </div>

        {query.isLoading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        ) : snippets.length === 0 ? (
          <p className="py-16 text-center text-muted-foreground text-sm">
            ຍັງບໍ່ມີ snippet
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {snippets.map((snippet) => (
              <div
                key={snippet.id}
                className="brand-card flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{snippet.title}</p>
                  <p className="mt-1 whitespace-pre-wrap text-muted-foreground text-sm">
                    {snippet.body}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={() => handleCopy(snippet.body)}
                  >
                    <Copy data-icon="inline-start" />
                    Copy
                  </Button>
                  {canManage ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        onClick={() => openEdit(snippet)}
                      >
                        ແກ້ໄຂ
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full border-destructive/40 text-destructive"
                        onClick={() => handleDelete(snippet.id)}
                      >
                        <Trash data-icon="inline-start" />
                      </Button>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </Main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? "ແກ້ໄຂ snippet" : "ເພີ່ມ snippet"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <FieldLayout>
              <FieldLabel>ຊື່</FieldLabel>
              <FieldContent>
                <Input
                  value={form.title}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, title: e.target.value }))
                  }
                  placeholder="ທັກທາຍລູກຄ້າ"
                />
              </FieldContent>
            </FieldLayout>
            <FieldLayout>
              <FieldLabel>ຂໍ້ຄວາມ</FieldLabel>
              <FieldContent>
                <Textarea
                  value={form.body}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, body: e.target.value }))
                  }
                  rows={5}
                />
              </FieldContent>
            </FieldLayout>
            <FieldLayout>
              <FieldLabel>ລຳດັບ</FieldLabel>
              <FieldContent>
                <Input
                  value={form.sortOrder}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, sortOrder: e.target.value }))
                  }
                  inputMode="numeric"
                />
              </FieldContent>
            </FieldLayout>
          </div>
          <DialogFooter>
            <Button
              className="rounded-full"
              isLoading={createSnippet.isPending || updateSnippet.isPending}
              onClick={handleSave}
            >
              ບັນທຶກ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
